import { motion } from "framer-motion";
import { Compass, Code2, LifeBuoy, ArrowUpRight } from "lucide-react";
import Reveal from "./Reveal";

const SERVICES = [
  {
    icon: Compass,
    tag: "01",
    title: "Business Consulting",
    desc: "Analyze business operations to identify critical bottlenecks and deploy scalable systems that drive efficiency, productivity, and long term operational excellence.",
    points: [
      "Business Process Analysis",
      "Technology & Systems Implementation",
      "Performance Optimization",
    ],
  },
  {
    icon: Code2,
    tag: "02",
    title: "Program Development",
    desc: "End-to-end custom software, web platforms, APIs and internal tools. Clean, tested code shipped in tight, transparent iterations.",
    points: ["Web & API platforms", "Automation tooling", "Legacy modernisation"],
    image:
      "https://images.pexels.com/photos/113850/pexels-photo-113850.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    icon: LifeBuoy,
    tag: "03",
    title: "Ongoing Support",
    desc: "Proactive maintenance, monitoring and evolution. We keep your systems fast and dependable so your team can focus on growth.",
    points: ["24/7 monitoring", "Performance tuning", "Feature evolution"],
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32 lg:py-40"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Reveal className="max-w-3xl mb-16 md:mb-20">
          <div className="mb-5 flex items-center gap-3">
            <span className="h-px w-12 bg-[#00F0FF]" />
            <span className="text-sm uppercase tracking-[0.25em] text-[#00F0FF]">
              What we do
            </span>
          </div>
          <h2 className="font-display font-bold tracking-tighter text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
            Three ways we help you ship better technology.
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.title} delay={i * 0.12}>
                <motion.div
                  data-testid={`service-card-${i}`}
                  whileHover={{ y: -8 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className="group h-full rounded-2xl border border-white/10 bg-[#0C1322] p-8 lg:p-10 transition-colors duration-300 hover:border-[#00F0FF]/60"
                >
                  <div className="flex items-start justify-between">
                    <span className="grid place-items-center h-14 w-14 rounded-xl bg-white/5 border border-white/10 text-[#00F0FF] transition-colors duration-300 group-hover:bg-[#00F0FF] group-hover:text-[#050A15]">
                      <Icon size={26} />
                    </span>
                    <span className="font-display text-5xl font-black text-white/5 leading-none">
                      {s.tag}
                    </span>
                  </div>

                  {s.image && (
                    <div className="mt-8 overflow-hidden rounded-xl border border-white/10">
                      <img
                        src={s.image}
                        alt={s.title}
                        className="h-36 w-full object-cover opacity-80 transition-all duration-500 group-hover:opacity-100 group-hover:scale-105"
                      />
                    </div>
                  )}

                  <h3 className="mt-8 font-display text-2xl md:text-3xl font-bold text-white">
                    {s.title}
                  </h3>
                  <p className="mt-4 text-[#94A3B8] font-light leading-relaxed">
                    {s.desc}
                  </p>

                  <ul className="mt-6 space-y-2.5">
                    {s.points.map((p) => (
                      <li
                        key={p}
                        className="flex items-center gap-3 text-sm text-white/80"
                      >
                        <ArrowUpRight size={15} className="text-[#00F0FF]" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
